import React, { useState, useEffect } from "react";
import { Modal, Upload, Button, message, Progress, Space } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";
import styles from "./index.module.less";
import pic1 from "@/assets/img/abianzu241.png";
import pic2 from "@/assets/img/xls.png";
const { Dragger } = Upload;

interface ImportModalProps {
  open: boolean;
  title?: string;
  onCancel: () => void;
  onComplete: () => void;
  importApi: (file: FormData) => Promise<any>;
  templateApi: () => Promise<any>;
  errorExcelApi?: (errorList: any[]) => Promise<any>;
  maxSize?: number; // MB
}

interface ImportInfo {
  successCount: number;
  errorCount: number;
  errorList: any[];
  hasError:boolean;
}

const ImportModal: React.FC<ImportModalProps> = ({
  open,
  title = "批量导入",
  onCancel,
  onComplete,
  importApi,
  templateApi,
  errorExcelApi,
  maxSize = 10,
}) => {
  const [step, setStep] = useState<number>(1); // 1: 未上传, 2: 准备上传, 3: 上传完成
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importInfo, setImportInfo] = useState<ImportInfo>({
    successCount: 0,
    errorCount: 0,
    errorList: [],
    hasError:false
  });
  const [uploading, setUploading] = useState(false);

  // 允许的文件类型配置
  const ALLOWED_TYPES = [
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  ];

  const ALLOWED_EXTENSIONS = [".xls", ".xlsx"];

  /**
   * 验证文件类型
   */
  const validateFileType = (file: File): boolean => {
    // 1. 检查文件扩展名
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      message.error("文件格式不正确，只支持 .xls 和 .xlsx 格式");
      return false;
    }

    // 2. 检查 MIME 类型
    if (file.type && !ALLOWED_TYPES.includes(file.type)) {
      message.error("文件类型不正确，请上传 Excel 文件");
      return false;
    }

    // 3. 检查文件大小
    const maxSizeInBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }

    return true;
  };

  /**
   * 下载模板
   */
  const downloadTemplate = async () => {
    try {
      const response = await templateApi(); 
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "导入模板.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("下载模板失败");
    }
  };

  /**
   * 下载错误文件
   */
  const downloadErrorFile = async () => {
    if (!errorExcelApi || importInfo.errorList.length === 0) return;

    try {
      const response = await errorExcelApi(importInfo.errorList);
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "导入失败信息.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("下载错误文件失败");
    }
  };

  /**
   * 文件上传配置
   */
  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".xls,.xlsx",
    fileList: fileList,
    beforeUpload: (file) => {
      if (!validateFileType(file)) {
        return false;
      }
      setFileList([file]);
      setStep(2);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
      setStep(1);
    },
  };

  /**
   * 重新上传
   */
  const handleReset = () => {
    setStep(1);
    setFileList([]);
    setImportInfo({
      successCount: 0,
      errorCount: 0,
      errorList: [],
      hasError:false
    });
  };

  /**
   * 确认导入
   */
  const handleConfirm = async () => {
    if (fileList.length === 0) {
      message.error("请先选择文件");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", fileList[0] as any);

      const response = await importApi(formData); 
      // const response = {
      //   data:{
      //     successCount:20,
      //     errorCount:20,
      //     errorList:[],
      //     hasError:false
      //   }
      // }
      setImportInfo({
        successCount: response.data.successCount || 0,
        errorCount: response.data.errorCount || 0,
        errorList: response.data.errorList || [],
        hasError:response.data.hasError
      });

      setStep(3);
      onComplete(); 

      message.success("导入完成");
    } catch (error) {
      message.error("导入失败，请重试");
    } finally {
      setUploading(false);
    }
  };
  //下载错误文件
  const handleDownError = async ()=>{
     await downloadErrorFile();
  }
  /**
   * 监听弹窗关闭状态
   */
  useEffect(() => {
    if (open) {
      handleReset();
    }
  }, [open]);

  /**
   * 渲染步骤1：上传文件
   */
  const renderStep1 = () => (
    <div className={styles.step1}>
      <p className={styles.downloadTip}>
        请下载标准模板填写数据，
        <a onClick={downloadTemplate} className="text-primay">
          下载模板
        </a>
      </p>
      <Dragger {...uploadProps} className={styles.uploader}>
        <p className="ant-upload-drag-icon">
          <img src={pic1} alt="" />
        </p>
        <p className="ant-upload-text mt-20">
          将文件托拽到此处或<a className="text-primay">上传文件</a>
        </p>
        <p className="ant-upload-hint">注：文档支持xls/xlsx两种格式</p>
      </Dragger>
    </div>
  );

  /**
   * 渲染步骤2：准备上传
   */
  const renderStep2 = () => (
    <div className={styles.step2}>
      <div className={styles.filePreview}>
        <img src={pic2} alt="Excel文件" />
        <div className={styles.fileName}>{fileList[0]?.name}</div>
      </div>
    </div>
  );

  /**
   * 渲染步骤3：上传完成
   */
  const renderStep3 = () => (
    <div className={styles.step3}>
       
      <div className={styles.importResult}>
        <div className={styles.resultItem}>
          <CheckCircleFilled className={styles.successIcon} /> 
          <span>上传成功{importInfo.successCount}条数据···</span>
        </div>
        {importInfo.errorCount > 0 && (
          <div className={styles.resultItem}>
            <ExclamationCircleFilled className={styles.errorIcon} /> 
            <span>上传失败{importInfo.errorCount}条数据···</span>
          </div>
        )}
      </div>
    </div>
  );

  /**
   * 渲染底部按钮
   */
  const renderFooter = () => {
    if (step === 2) {
      return (
        <div className={styles.footer}>
          <Space>
            <Button className="btn-2" onClick={handleReset}>
              重新上传
            </Button>
            <Button type="primary" onClick={handleConfirm} loading={uploading}>
              导入
            </Button>
          </Space>
        </div>
      );
    }else if (step === 3) {
      return (
        <div className={styles.footer}>
          <Space>
            <Button className="btn-2" onClick={onCancel}>
              关闭
            </Button>
            {importInfo.errorCount>0 && (
            <Button type="primary" onClick={handleDownError} loading={uploading}>
              下载错误文件
            </Button>
            )}
          </Space>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={renderFooter()}
      width={500}
    >
      <div className={styles.importModal}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </Modal>
  );
};

export default ImportModal;
